import PropTypes from 'prop-types';
import {withHandlers, compose, setPropTypes} from 'recompose';
import {graphql} from 'react-apollo';
import every from 'lodash/every';
import fromPairs from 'lodash/fromPairs';
import {DELETE_ROW_MUTATION} from '../../queries';
import buildRowInput from './_buildRowInput';
// import makeBlankRow from './_makeBlankRow'

// Queue-key prefix used to identify row-delete items in pendingSaves.  Kept in
// sync with flushPendingSaves.js, which is the only other place that inspects
// this key shape.
export const DELETE_ROW_QUEUE_PREFIX = 'row-delete:';
export const rowDeleteKey = rowId => `${DELETE_ROW_QUEUE_PREFIX}${rowId}`;

export default compose(
  setPropTypes({
    showConfirmation: PropTypes.func.isRequired
  }),
  graphql(DELETE_ROW_MUTATION, {
    props: ({
      ownProps: {setSavePending, setError, updateTimesheetData, timesheet},
      mutate
    }) => ({
      deleteRowData(rowIndex) {
        // delete row data on the server
        const row = timesheet.rows[rowIndex];
        setSavePending(fromPairs(row.entries.map(e => [e.id, true])));
        mutate({
          variables: {
            ...buildRowInput(timesheet, timesheet.rows[rowIndex])
          },
          update(store, {data: {deleteTimesheetRow}}) {
            if (!deleteTimesheetRow) {
              setTimeout(() => {
                setError('Delete failed');
              });
            } else {
              updateTimesheetData({rows: {$splice: [[rowIndex, 1]]}}, store);
            }
          }
        })
          .catch(err => {
            setError(err);
          })
          .then(() => {
            setSavePending(fromPairs(row.entries.map(e => [e.id, false])));
          });
      }
    })
  }),
  withHandlers({
    // Queues a row delete for the next batched save instead of firing the
    // mutation immediately.  Called from the confirmation dialog success
    // action when auto-save is OFF.  The row stays visible (with a
    // strike-through style) until the user clicks Save, so they can still see
    // what they've marked for deletion and cancel via page navigation if
    // needed.
    queueRowDelete: ({
      timesheet,
      queuePendingSave,
      clearPendingSave,
      pendingSaves
    }) => rowIndex => {
      const row = timesheet.rows[rowIndex];
      if (!row) return;

      // Sweep out any cell edits already queued for this row - the row delete
      // supersedes them, so sending them would be a waste and could conflict
      // with the delete on the server.
      if (clearPendingSave && pendingSaves) {
        Object.keys(pendingSaves).forEach(key => {
          const item = pendingSaves[key];
          if (!item || !item.variables || !item.variables.row) return;
          if (item.variables.row.id === row.id) clearPendingSave(key);
        });
      }

      queuePendingSave({
        kind: 'deleteRow',
        key: rowDeleteKey(row.id),
        rowId: row.id,
        variables: buildRowInput(timesheet, row)
      });
    }
  }),
  withHandlers({
    deleteRow: ({
      timesheet,
      deleteRowData,
      queueRowDelete,
      showConfirmation,
      updateTimesheetData,
      autoSave
    }) => rowIndex => {
      // check if row is transient.
      // if it is, update timesheet data to remove it
      // otherwise, request confirmation
      const transient = every(timesheet.rows[rowIndex].entries, e =>
        e.id.startsWith('TMP')
      );
      if (transient) {
        // updateTimesheetData({rows: { [rowIndex]: { $set: makeBlankRow(timesheet) } } })
        updateTimesheetData({rows: {$splice: [[rowIndex, 1]]}});
        return;
      }

      // Auto-save OFF: queue the delete so the user's "Save" click commits
      // it alongside the rest of their pending edits.  Copy tweaked so the
      // user understands the delete is deferred.
      const autoSaveOff = autoSave === false;
      const successAction = autoSaveOff
        ? ['queueRowDelete', rowIndex]
        : ['deleteRowData', rowIndex];
      const message = autoSaveOff
        ? 'Auto-save is off, so this row will be marked for deletion and removed when you click Save. Continue?'
        : 'Are you sure you wish to delete this row?';

      showConfirmation({
        title: 'Delete Timesheet Row',
        message,
        successAction
      });
    }
  })
);
